// client/src/components/company/NewRequest.js
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { sanitizeInput } from '../utils/sanitize';
import { parseCurrencyInput, handleCurrencyInputChange, availableCurrencies, getCurrencySymbol } from '../utils/formatUtils';
import { PlusCircle, Building2, Globe } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const NewRequest = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { type } = useParams();
  const isHotMoney = type === 'hot-money';

  // Recupera isPaying dall'organizzazione dell'utente
  // Se isPaying non esiste, considera come true
  const isPaying = user?.organization?.isPaying !== false;
  
  const [formData, setFormData] = useState({
    amount: '',
    currency: 'EUR',
    rateTypes: ['fixed', 'floating'],
    duration: '',
    customStartDate: '',
    customEndDate: '',
    withdrawalType: '',
    bidDeadline: '',
    bidDeadlineTime: '', // Campo orario opzionale (default 23:59 se vuoto)
    notes: '',
    targetBanks: isPaying ? 'custom' : 'third_party',
    selectedBanks: [] // Array di userIds
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [affiliatedBanks, setAffiliatedBanks] = useState([]);
  const [thirdPartyBanks, setThirdPartyBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(true);
  // renewalFlags: { [organizationId]: boolean }
  const [renewalFlags, setRenewalFlags] = useState({});
  const navigate = useNavigate();
  
  const { amount, currency, rateTypes, duration, customStartDate, customEndDate, withdrawalType, bidDeadline, bidDeadlineTime, notes, targetBanks, selectedBanks } = formData;
  
  // Helper function to get bank display name (must be before useMemo)
  const getBankDisplayName = (bank) => {
    if (bank.referralDetails?.bankName) {
      return bank.referralDetails.bankName;
    }
    if (bank.bankName) {
      return bank.bankName;
    }
    return `${bank.firstName || ''} ${bank.lastName || ''}`.trim();
  };
  
  // ✅ NUOVO: Raggruppa le banche per bankName
  const groupedBanks = useMemo(() => {
    const groups = {};
    
    affiliatedBanks.forEach(bank => {
      const bankName = getBankDisplayName(bank);
      
      if (!groups[bankName]) {
        groups[bankName] = {
          bankName,
          users: [],
          organizationId: bank.organizationId
        };
      }
      
      groups[bankName].users.push({
        userId: bank._id || bank.userId,
        firstName: bank.firstName,
        lastName: bank.lastName,
        email: bank.email
      });
    });
    
    return Object.values(groups);
  }, [affiliatedBanks]);

  const groupedThirdPartyBanks = useMemo(() => {
  const groups = {};
  
  thirdPartyBanks.forEach(bank => {
    const orgName = bank.organization?.name || 'Unknown Bank';
    const orgId = bank.organization?._id || bank._id;
    
    if (!groups[orgId]) {
      groups[orgId] = {
        bankName: orgName,
        organizationId: orgId,
        users: []
      };
    }
    
    groups[orgId].users.push({
      userId: bank._id,
      firstName: bank.firstName,
      lastName: bank.lastName,
      email: bank.email
    });
  });
  
  return Object.values(groups);
}, [thirdPartyBanks]);
    
  useEffect(() => {
    const fetchAffiliatedBanks = async () => {
      try {
        setLoadingBanks(true);

        const response = await axios.get('/api/company/bank-referrals');
        setAffiliatedBanks(response.data.bankReferrals || []);

        if (isPaying) {
          const tpResponse = await axios.get('/api/requests/third-party-banks');
          setThirdPartyBanks((tpResponse.data.thirdPartyBanks || []).filter(bank =>
            bank.organization?.name !== 'Banca Demo EO' &&
            bank.organization?.name !== 'Allianz Bank Financial Advisors'
          ));
        }
      } catch (err) {
        console.error('Error fetching banks:', err);
        setAffiliatedBanks([]);
        setThirdPartyBanks([]);
      } finally {
        setLoadingBanks(false);
      }
    };

    fetchAffiliatedBanks();
  }, [isPaying]);

useEffect(() => {
  if (!isPaying) {
    setFormData(prev => ({
      ...prev,
      targetBanks: 'third_party',
      selectedBanks: []
    }));
  }
}, [isPaying]);

const toggleThirdPartyBankSelection = (bankGroup) => {
  const userIds = bankGroup.users.map(u => u.userId);
  const allSelected = userIds.every(id => selectedBanks.includes(id));

  let updatedBanks;
  if (allSelected) {
    updatedBanks = selectedBanks.filter(id => !userIds.includes(id));
  } else {
    updatedBanks = [...new Set([...selectedBanks, ...userIds])];
  }

  setFormData({ ...formData, selectedBanks: updatedBanks });
};

const isAllRelationshipBanksSelected = groupedBanks.length > 0 && groupedBanks.every(bg =>
  bg.users.every(u => selectedBanks.includes(u.userId))
);

const toggleAllRelationshipBanks = () => {
  const allUserIds = groupedBanks.flatMap(bg => bg.users.map(u => u.userId));
  const updatedBanks = isAllRelationshipBanksSelected
    ? selectedBanks.filter(id => !allUserIds.includes(id))
    : [...new Set([...selectedBanks, ...allUserIds])];

  setFormData({ ...formData, selectedBanks: updatedBanks });
};

const isThirdPartyBankSelected = (bankGroup) => {
  const userIds = bankGroup.users.map(u => u.userId);
  return userIds.every(id => selectedBanks.includes(id));
};
  
  const onChange = e => {
    if (e.target.name === 'amount') {
      handleCurrencyInputChange(e, setFormData, formData, 'amount');
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  const onRateTypeChange = (rateType) => {
    const updatedRateTypes = rateTypes.includes(rateType)
      ? rateTypes.filter(type => type !== rateType)
      : [...rateTypes, rateType];
    
    setFormData({ ...formData, rateTypes: updatedRateTypes });
  };
  
  // ✅ MODIFICATO: Toggle per gruppo di banca (tutti gli utenti insieme)
  const toggleBankGroupSelection = (bankGroup) => {
    
    const userIds = bankGroup.users.map(u => u.userId);
    
    // Controlla se TUTTI gli utenti di questa banca sono già selezionati
    const allSelected = userIds.every(id => selectedBanks.includes(id));
    
    let updatedBanks;
    if (allSelected) {
      // Rimuovi TUTTI gli utenti di questa banca
      updatedBanks = selectedBanks.filter(id => !userIds.includes(id));
    } else {
      // Aggiungi TUTTI gli utenti di questa banca
      updatedBanks = [...new Set([...selectedBanks, ...userIds])];
    }
    
    setFormData({ ...formData, selectedBanks: updatedBanks });
  };
  
  // ✅ NUOVO: Controlla se un gruppo è selezionato
  const isBankGroupSelected = (bankGroup) => {
    const userIds = bankGroup.users.map(u => u.userId);
    return userIds.every(id => selectedBanks.includes(id));
  };
  
  const toggleRenewalFlag = (organizationId) => {
    setRenewalFlags(prev => ({
      ...prev,
      [organizationId]: !prev[organizationId]
    }));
  };

  const onDurationChange = e => {
    const newDuration = e.target.value;
    let newWithdrawalType = withdrawalType;

    if (newDuration === 'overnight') {
      newWithdrawalType = 'demand';
    }

    setFormData({
      ...formData,
      duration: newDuration,
      withdrawalType: newWithdrawalType,
      // Clear custom dates when switching away from custom
      customStartDate: newDuration === 'custom' ? formData.customStartDate : '',
      customEndDate: newDuration === 'custom' ? formData.customEndDate : ''
    });
  };
  
  const onSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    const formattedAmount = parseCurrencyInput(amount);
    
    if (isNaN(formattedAmount) || formattedAmount <= 0) {
      setError(t('forms.validation.validAmount'));
      setLoading(false);
      return;
    }
    
    if (rateTypes.length === 0) {
      setError(t('forms.validation.selectRateType'));
      setLoading(false);
      return;
    }
    
    // Validate custom dates
    if (duration === 'custom') {
      if (!customStartDate || !customEndDate) {
        setError(t('requests.customDatesRequired'));
        setLoading(false);
        return;
      }
      if (new Date(customStartDate) < new Date(formatDate())) {
        setError(t('requests.customStartFuture'));
        setLoading(false);
        return;
      }
      if (new Date(customEndDate) <= new Date(customStartDate)) {
        setError(t('requests.customEndAfterStart'));
        setLoading(false);
        return;
      }
    }

    if (!isHotMoney && !withdrawalType) {
      setError(t('forms.validation.validWithdrawal'));
      setLoading(false);
      return;
    }
    
    if (affiliatedBanks.length > 0 && targetBanks === 'affiliated' && selectedBanks.length === 0) {
      setError(t('requests.selectAtLeastOneBank') || 'Please select at least one bank');
      setLoading(false);
      return;
    }
    
    try {
      // Build bankRenewalFlags from renewalFlags state (only relationship banks marked as renewal)
      const bankRenewalFlagsPayload = isPaying
        ? groupedBanks
            .filter(bg => bg.organizationId && renewalFlags[bg.organizationId])
            .map(bg => ({ bankOrganization: bg.organizationId, isRenewal: true }))
        : [];

      const requestData = {
        amount: formattedAmount,
        currency,
        rateTypes,
        duration,
        ...(duration === 'custom' ? { customStartDate, customEndDate } : {}),
        withdrawalType: isHotMoney ? 'demand' : withdrawalType,
        bidDeadline,
        bidDeadlineTime: bidDeadlineTime || '',
        notes: sanitizeInput(notes),
        targetBanks: isPaying ? 'custom' : 'third_party',
        selectedBanks: isPaying ? selectedBanks : [],
        bankRenewalFlags: isHotMoney ? [] : bankRenewalFlagsPayload,
        requestType: isHotMoney ? 'hot_money' : 'allocation'
      };

      await axios.post('/api/requests', requestData);
      navigate(`/company/past-requests/${isHotMoney ? 'hot-money' : 'allocation'}`);
    } catch (err) {
      setError(err.response?.data?.message || t('common.error.createRequest', { 
        defaultValue: 'Failed to create request' 
      }));
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    let month = today.getMonth() + 1;
    let day = today.getDate();
    
    month = month < 10 ? `0${month}` : month;
    day = day < 10 ? `0${day}` : day;
    
    return `${year}-${month}-${day}`;
  };

  const getWithdrawalOptions = () => {
    if (duration === 'overnight') {
      return [
        { value: 'demand', label: t('requests.demand') }
      ];
    }
    return [
      { value: 'early_allowed', label: t('requests.earlyWithdrawalAllowed') },
      { value: 'no_withdrawal', label: t('requests.noWithdrawalAllowed') }
    ];
  };
  
  return (
    <div className="company-dashboard">
      <div className="card">
        <div className="card-title">
          <PlusCircle className="card-title-icon" size={20} />
          <span>{isHotMoney ? t('requests.newHotMoneyRequest') : t('requests.newLiquidityRequest')}</span>
        </div>
        
        {error && <div className="alert alert-danger">{error}</div>}
        
        <form id="new-request-form" className="request-form" onSubmit={onSubmit}>
          <div className="form-section-head">
            <span className="t">{t('requests.sectionAmount')}</span>
            <span className="rule" />
          </div>

          <div className="form-row currency-amount">
            <div className="form-group">
              <label htmlFor="currency">
                {t('requests.currency') || 'Currency'} <span className="req">*</span>
              </label>
              <select
                id="currency"
                name="currency"
                className="form-select-custom"
                value={currency}
                onChange={onChange}
                required
                disabled={!isPaying}
                style={!isPaying ? { backgroundColor: '#e9ecef', color: '#6c757d', cursor: 'not-allowed' } : {}}
              >
                {availableCurrencies
                  .filter(curr => isPaying || curr.code === 'EUR')
                  .map(curr => (
                    <option key={curr.code} value={curr.code}>
                      {curr.code}
                    </option>
                  ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="amount">
                {t('requests.totalAmount')} <span className="req">*</span>
              </label>
              <div className="amount-field">
                <span className="sym">{getCurrencySymbol(currency)}</span>
                <input
                  type="text"
                  id="amount"
                  name="amount"
                  placeholder={t('forms.enterAmount')}
                  value={amount}
                  onChange={onChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section-head">
            <span className="t">{t('requests.sectionConditions')}</span>
            <span className="rule" />
          </div>

          {/* Rate type selection - only for paying customers, non-paying default to both fixed and floating */}
          {isPaying && (
            <div className="form-group">
              <label>
                {t('requests.acceptedRateTypesNR')} <span className="req">*</span>
              </label>
              <div className="chip-group">
                <label className={"chip-check" + (rateTypes.includes('fixed') ? " on" : "")}>
                  <span className="box">✓</span>
                  <span>{t('requests.fixed')}</span>
                  <input
                    type="checkbox"
                    id="rateType-fixed"
                    checked={rateTypes.includes('fixed')}
                    onChange={() => onRateTypeChange('fixed')}
                  />
                </label>
                <label className={"chip-check" + (rateTypes.includes('floating') ? " on" : "")}>
                  <span className="box">✓</span>
                  <span>{t('requests.floating')}</span>
                  <input
                    type="checkbox"
                    id="rateType-floating"
                    checked={rateTypes.includes('floating')}
                    onChange={() => onRateTypeChange('floating')}
                  />
                </label>
              </div>
              <div className="field-help dark">
                <span>{t('requests.rateTypesHelp')}</span>
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="duration">
              {t('requests.maturity')} <span className="req">*</span>
            </label>
            <select
              id="duration"
              name="duration"
              className="form-select-custom"
              value={duration}
              onChange={onDurationChange}
              required
            >
              <option value="">{t('requests.selectMaturity')}</option>
              {isPaying && <option value="custom">{t('requests.custom')}</option>}
              <option value="overnight">{t('requests.overnight')}</option>
              {/* ✅ CONDIZIONA: 7d, 14d, 21d solo per paganti */}
              {isPaying && <option value="7d">7 {t('common.days', { defaultValue: 'days' })}</option>}
              {isPaying && <option value="14d">14 {t('common.days', { defaultValue: 'days' })}</option>}
              {isPaying && <option value="21d">21 {t('common.days', { defaultValue: 'days' })}</option>}
              {/* ✅ CONDIZIONA: 1 e 2 mesi solo per paganti */}
              {isPaying && <option value="1">1 {t('common.month', { defaultValue: 'month' })}</option>}
              {isPaying && <option value="2">2 {t('common.months', { defaultValue: 'months' })}</option>}
              <option value="3">3 {t('common.months', { defaultValue: 'months' })}</option>
              <option value="6">6 {t('common.months', { defaultValue: 'months' })}</option>
              {!isHotMoney && <option value="12">12 {t('common.months', { defaultValue: 'months' })}</option>}
              {!isHotMoney && <option value="18">18 {t('common.months', { defaultValue: 'months' })}</option>}
              {!isHotMoney && <option value="24">24 {t('common.months', { defaultValue: 'months' })}</option>}
            </select>
          </div>

          {/* Custom maturity date inputs - only when custom is selected */}
          {duration === 'custom' && (
            <div className="form-row two">
              <div className="form-group">
                <label htmlFor="customStartDate">
                  {t('requests.customStartDate')} <span className="req">*</span>
                </label>
                <input
                  type="date"
                  id="customStartDate"
                  name="customStartDate"
                  value={customStartDate}
                  onChange={onChange}
                  min={formatDate()}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="customEndDate">
                  {t('requests.customEndDate')} <span className="req">*</span>
                </label>
                <input
                  type="date"
                  id="customEndDate"
                  name="customEndDate"
                  value={customEndDate}
                  onChange={onChange}
                  min={customStartDate || formatDate()}
                  required
                />
              </div>
            </div>
          )}

          {!isHotMoney && (
            <div className="form-group">
              <label htmlFor="withdrawalType">
                {t('requests.withdrawalOptions')} <span className="req">*</span>
              </label>
              <select
                id="withdrawalType"
                name="withdrawalType"
                className="form-select-custom"
                value={withdrawalType}
                onChange={onChange}
                required
                disabled={duration === 'overnight'}
              >
                <option value="">{t('requests.selectWithdrawalOption')}</option>
                {getWithdrawalOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {duration === 'overnight' && (
                <div className="field-help">
                  <span className="ico">i</span>
                  <span>
                    {t('requests.overnightAutoSet', {
                      defaultValue: 'Overnight deposits are automatically set to "Demand"'
                    })}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Sezione banche: per isPaying, sempre visibile (relationship sempre, third party solo per allocation) */}
          {!loadingBanks && isPaying && (
            <>
              <div className="form-section-head">
                <span className="t">{t('requests.sectionTargetBanks')}</span>
                <span className="rule" />
                <span className="count">
                  {groupedBanks.filter(bg => bg.users.some(u => selectedBanks.includes(u.userId))).length +
                    (isHotMoney ? 0 : groupedThirdPartyBanks.filter(bg => bg.users.some(u => selectedBanks.includes(u.userId))).length)} {t('requests.banksSelected') || 'bank(s) selected'}
                </span>
              </div>

              {/* Relationship Banks — visibili per tutti i tipi */}
              {groupedBanks.length > 0 && (
                <div className="bank-panel">
                  <div className="bank-panel-head">
                    <Building2 size={14} color="#35507d" />
                    <span className="title">{t('requests.relationshipBanks') || 'Relationship Banks'}</span>
                    <button
                      type="button"
                      className="select-all"
                      onClick={toggleAllRelationshipBanks}
                    >
                      {isAllRelationshipBanksSelected ? t('requests.deselectAllBanks') : t('requests.selectAllBanks')}
                    </button>
                  </div>
                  {groupedBanks.map((bankGroup, index) => (
                    <div
                      key={`rel-${index}`}
                      className={"bank-row" + (isBankGroupSelected(bankGroup) ? " on" : "")}
                      onClick={() => toggleBankGroupSelection(bankGroup)}
                    >
                      <span className="box">✓</span>
                      <span className="name">{bankGroup.bankName}</span>
                      {/* Renewal toggle — solo per allocation */}
                      {!isHotMoney && (
                        <label
                          className={"renew-toggle" + (renewalFlags[bankGroup.organizationId] ? " on" : "")}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="sw" />
                          {t('requests.renewalFlag')}
                          <input
                            type="checkbox"
                            checked={!!renewalFlags[bankGroup.organizationId]}
                            onChange={() => toggleRenewalFlag(bankGroup.organizationId)}
                          />
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Third Party Banks — solo per allocation, non per hot_money */}
              {!isHotMoney && groupedThirdPartyBanks.length > 0 && (
                <div className="bank-panel">
                  <div className="bank-panel-head" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '3px' }}>
                    <span className="title">
                      <Globe size={14} color="#35507d" style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                      {t('requests.thirdPartyBanks') || 'Third Party Banks'}
                    </span>
                    <span className="note">{t('requests.thirdPartyBanksInfo')}</span>
                  </div>
                  {groupedThirdPartyBanks.map((bankGroup, index) => (
                    <div
                      key={`tp-${index}`}
                      className={"bank-row" + (isThirdPartyBankSelected(bankGroup) ? " on" : "")}
                      onClick={() => toggleThirdPartyBankSelection(bankGroup)}
                    >
                      <span className="box">✓</span>
                      <span className="name">{bankGroup.bankName}</span>
                      <span
                        className="info"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/company/bank-info/${encodeURIComponent(bankGroup.bankName)}`);
                        }}
                      >
                        i
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {loadingBanks && (
            <div className="form-group">
              <small style={{ color: '#6c757d' }}>
                {t('common.loading') || 'Loading affiliated banks...'}
              </small>
            </div>
          )}
          
          <div className="form-section-head">
            <span className="t">{t('requests.sectionDeadlineNotes')}</span>
            <span className="rule" />
          </div>

          <div className="form-row deadline">
            <div className="form-group">
              <label htmlFor="bidDeadline">
                {t('requests.bidSubmissionDeadline')} <span className="req">*</span>
              </label>
              <input
                type="date"
                id="bidDeadline"
                name="bidDeadline"
                value={bidDeadline}
                onChange={onChange}
                min={formatDate()}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="bidDeadlineTime">
                {t('requests.bidTimeLabel')}
              </label>
              <input
                type="time"
                id="bidDeadlineTime"
                name="bidDeadlineTime"
                value={bidDeadlineTime}
                onChange={onChange}
                placeholder="23:59"
              />
              <div className="field-help sm">
                <span>{t('requests.deadlineTimeHelp', { defaultValue: 'Default: 23:59 if not specified' })}</span>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notes">
              {t('requests.additionalNotes')}
            </label>
            <textarea
              id="notes"
              name="notes"
              rows="4"
              placeholder={t('forms.additionalRequirements')}
              value={notes}
              onChange={onChange}
            ></textarea>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn btn-light"
              onClick={() => navigate('/company/dashboard')}
            >
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? t('actions.submitting') : t('common.submitRequest')}
            </button>
          </div>
        </form>
      </div>
    </div>   
  );
};

export default NewRequest;